import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, createBlock as _createBlock, vShow as _vShow, resolveDirective as _resolveDirective, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "dialog-header" }
const _hoisted_2 = { class: "dialog-body" }
const _hoisted_3 = { class: "search-bar" }
const _hoisted_4 = { class: "new-chat-btn" }
const _hoisted_5 = {
  key: 0,
  class: "text-center mt-4"
}
const _hoisted_6 = {
  key: 1,
  class: "conversation-groups"
}
const _hoisted_7 = { class: "group-label" }
const _hoisted_8 = { class: "group-items" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "history-item-title" }
const _hoisted_11 = {
  key: 0,
  class: "empty-state"
}

import { Agent } from '@/services/agentService';
import {
  type Conversation,
  fetchConversations,
  searchConversations,
} from '@/services/conversationService';
import { useConversationStore } from '@/stores/conversationStore';
import { PaginatedCache } from '@/utils/cache/PaginatedCache';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

interface ConversationGroup {
  label: string;
  conversations: Conversation[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchChatsDialog',
  props: {
    modelValue: { type: Boolean },
    agents: {}
  },
  emits: ["update:modelValue", "select-conversation", "new-chat"],
  setup(__props: any, { emit: __emit }) {

const dialogCache = new PaginatedCache<Conversation>({
  ttl: 5 * 60 * 1000,
  storageKeyPrefix: 'search-dialog-conversations',
});
const CACHE_KEY = dialogCache.generateKey({ limit: 100, offset: 0 });

const props = __props;

const emit = __emit;

const route = useRoute();
const conversationStore = useConversationStore();

const searchQuery = ref('');
const searchResults = ref<Conversation[]>([]);
const allConversations = ref<Conversation[]>([]);
const isLoading = ref(false);
const expanded = ref(false);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const conversations = computed(() => {
  if (searchQuery.value.trim()) {
    return searchResults.value;
  }
  return allConversations.value;
});

const loadAllConversations = async () => {
  try {
    const cached = await dialogCache.get(CACHE_KEY);
    if (cached) {
      allConversations.value = cached.data;
      return;
    }

    isLoading.value = true;
    const response = await fetchConversations(100, 0);
    allConversations.value = response.data;
    await dialogCache.set(CACHE_KEY, {
      data: response.data,
      total: response.total,
      hasMore: response.has_more,
      timestamp: Date.now(),
    });
  } catch {
    allConversations.value = [];
  } finally {
    isLoading.value = false;
  }
};

const groupedConversations = computed((): ConversationGroup[] => {
  const groups: ConversationGroup[] = [];
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sevenDaysAgo = new Date(todayStart);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const today: Conversation[] = [];
  const previous7Days: Conversation[] = [];
  const older: Conversation[] = [];

  for (const conv of conversations.value) {
    const convDate = new Date(conv.update_time || conv.create_time);
    if (convDate >= todayStart) {
      today.push(conv);
    } else if (convDate >= sevenDaysAgo) {
      previous7Days.push(conv);
    } else {
      older.push(conv);
    }
  }

  if (today.length > 0) {
    groups.push({ label: 'Today', conversations: today });
  }
  if (previous7Days.length > 0) {
    groups.push({ label: 'Previous 7 Days', conversations: previous7Days });
  }
  if (older.length > 0) {
    groups.push({ label: 'Older', conversations: older });
  }

  return groups;
});

const handleSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  const query = searchQuery.value.trim();
  if (!query) {
    searchResults.value = [];
    return;
  }

  searchTimeout = setTimeout(async () => {
    try {
      isLoading.value = true;
      const response = await searchConversations(query);
      searchResults.value = response.data;
    } catch (error) {
      console.error('Search failed:', error);
      searchResults.value = [];
    } finally {
      isLoading.value = false;
    }
  }, 300);
};

const handleSelectConversation = (conversation: Conversation) => {
  if (route.params.id === conversation.id) return;

  conversationStore.setAgentFilter(null);
  emit('select-conversation', conversation);
  emit('update:modelValue', false);
  resetState();
};

const handleNewChat = (agent: Agent) => {
  emit('new-chat', agent);
  emit('update:modelValue', false);
  resetState();
};

const handleOutsideClick = () => {
  if (expanded.value) {
    expanded.value = false;
  }
};

const resetState = () => {
  searchQuery.value = '';
  searchResults.value = [];
  expanded.value = false;
};

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      loadAllConversations();
    } else {
      resetState();
    }
  },
);

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    "model-value": _ctx.modelValue,
    "max-width": "723",
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('update:modelValue', $event)))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _cache[5] || (_cache[5] = _createElementVNode("span", { class: "dialog-title" }, "Search Chat", -1)),
            _createVNode(_component_v_btn, {
              icon: "",
              size: "small",
              variant: "text",
              "aria-label": "Close search dialog",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('update:modelValue', false)))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, {
                  size: 40,
                  "aria-hidden": "true"
                }, {
                  default: _withCtx(() => _cache[4] || (_cache[4] = [
                    _createTextVNode("mdi-close-circle-outline", -1)
                  ])),
                  _: 1,
                  __: [4]
                })
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
                type: "text",
                class: "search-input",
                placeholder: "Search Here",
                onInput: handleSearch
              }, null, 544), [
                [_vModelText, searchQuery.value]
              ]),
              _createVNode(_component_v_icon, {
                size: 20,
                class: "search-icon",
                "aria-hidden": "true"
              }, {
                default: _withCtx(() => _cache[6] || (_cache[6] = [
                  _createTextVNode("mdi-magnify", -1)
                ])),
                _: 1,
                __: [6]
              })
            ]),
            _withDirectives((_openBlock(), _createElementBlock("div", {
              class: "new-chat-selector",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (expanded.value = !expanded.value))
            }, [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_v_icon, {
                  size: 24,
                  class: "new-chat-icon",
                  "aria-hidden": "true"
                }, {
                  default: _withCtx(() => _cache[7] || (_cache[7] = [
                    _createTextVNode("mdi-tooltip-edit-outline", -1)
                  ])),
                  _: 1,
                  __: [7]
                }),
                _cache[8] || (_cache[8] = _createElementVNode("span", { class: "new-chat-text" }, "New Chat", -1)),
                _createVNode(_component_v_icon, {
                  size: 24,
                  class: "new-chat-chevron",
                  "aria-hidden": "true"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(expanded.value ? 'mdi-chevron-up' : 'mdi-chevron-down'), 1)
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_component_v_expand_transition, null, {
                default: _withCtx(() => [
                  _withDirectives(_createElementVNode("div", null, [
                    _createVNode(_component_v_list, {
                      density: "compact",
                      class: "pa-0 agent-list"
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.agents, (agent) => {
                          return (_openBlock(), _createBlock(_component_v_list_item, {
                            key: agent.name,
                            class: "agent-item",
                            onClick: _withModifiers(($event: any) => (handleNewChat(agent)), ["stop"])
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item_title, { class: "agent-item-title" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(agent.name), 1)
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1032, ["onClick"]))
                        }), 128))
                      ]),
                      _: 1
                    })
                  ], 512), [
                    [_vShow, expanded.value]
                  ])
                ]),
                _: 1
              })
            ])), [
              [_directive_click_outside, handleOutsideClick]
            ]),
            (isLoading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(_component_v_progress_circular, {
                    indeterminate: "",
                    size: "24",
                    width: "2"
                  })
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedConversations.value, (group) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: group.label,
                      class: "conversation-group"
                    }, [
                      _createElementVNode("div", _hoisted_7, _toDisplayString(group.label), 1),
                      _createElementVNode("div", _hoisted_8, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.conversations, (conversation) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: conversation.id,
                            class: _normalizeClass([
                  'history-item',
                  {
                    'history-item--active':
                      _unref(route).name === 'conversation' &&
                      _unref(route).params.id === conversation.id,
                  },
                ]),
                            onClick: ($event: any) => (handleSelectConversation(conversation))
                          }, [
                            _createElementVNode("span", _hoisted_10, _toDisplayString(conversation.title || conversation.id), 1)
                          ], 10, _hoisted_9))
                        }), 128))
                      ])
                    ]))
                  }), 128)),
                  (groupedConversations.value.length === 0 && searchQuery.value.trim())
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, " No conversations found "))
                    : _createCommentVNode("", true)
                ]))
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})